import { FC, useEffect, useState, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import {
  StepViewModel,
  SimpleExpression,
  LogicalOperator,
  WaterfallRuleType,
  WaterfallStepAction,
  OverrideSettingViewModel,
  WaterfallRuleViewModel,
  IntegrationType,
  MetadataContextType,
  FieldMetadata,
  OperatorInfo,
} from '../../models/waterfall';
import IntegrationSettingsService from '../../services/integrationSettingsService';
import { IntegrationSettings } from '../../models/integrationSettings';
import * as waterfallService from '../../services/waterfallService';
import { RuleGroup } from './RuleGroup';
import { OverrideSettingForm } from './OverrideSettingForm';
import { filterFieldsByVendor } from './stepUtils';
import FormInputText from '../../components/form/FormInputText';
import FormDropdown from '../../components/form/FormDropdown';
import FormInputNumber from '../../components/form/FormInputNumber';
import FormRadioGroup from '../../components/form/FormRadioGroup';

interface StepDetailsDialogProps {
  step: StepViewModel | null;
  visible: boolean;
  onClose: () => void;
  onSave: (step: StepViewModel) => void;
  integrationType: IntegrationType;
}

interface StepFormValues {
  description: string;
  integrationSettingsId: number;
  stepTimeoutInSeconds?: number | null;
  onTimeoutExceededAction?: WaterfallStepAction;
  onFatalErrorAction?: WaterfallStepAction;
}

// Action options for radio groups
const actionOptions = [
  { label: 'Continue', value: WaterfallStepAction.Continue },
  { label: 'Abort', value: WaterfallStepAction.Abort },
];

const validationSchema = yup.object().shape({
  description: yup.string().trim().required('Step name is required'),
  integrationSettingsId: yup
    .number()
    .required('Vendor is required')
    .min(1, 'Vendor is required')
    .test('not-zero', 'Vendor is required', (value) => value !== 0),
  stepTimeoutInSeconds: yup.number().nullable().min(1, 'Must be at least 1 second').optional(),
  onTimeoutExceededAction: yup.mixed<WaterfallStepAction>().oneOf(Object.values(WaterfallStepAction)).optional(),
  onFatalErrorAction: yup.mixed<WaterfallStepAction>().oneOf(Object.values(WaterfallStepAction)).optional(),
});

export const StepDetailsDialog: FC<StepDetailsDialogProps> = ({ step, visible, onClose, onSave, integrationType }) => {
  const [integration, setIntegration] = useState<IntegrationSettings | null>(null);
  const [isLoadingIntegration, setIsLoadingIntegration] = useState(false);
  const [hasRuleChanges, setHasRuleChanges] = useState(false);
  const isInitialLoad = useRef(true);
  const [vendorOptions, setVendorOptions] = useState<{ label: string; value: number }[]>([]);
  const [waterfallContextFields, setWaterfallContextFields] = useState<FieldMetadata[]>([]);
  const [operators, setOperators] = useState<OperatorInfo[]>([]);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
    watch,
  } = useForm<StepFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      description: '',
      integrationSettingsId: 0,
      stepTimeoutInSeconds: undefined,
      onTimeoutExceededAction: WaterfallStepAction.Continue,
      onFatalErrorAction: WaterfallStepAction.Continue,
    },
  });

  const [rules, setRules] = useState<WaterfallRuleViewModel[]>([]);
  const addRuleMenuRef = useRef<Menu>(null);
  
  const selectedVendorId = watch('integrationSettingsId');

  const filteredWaterfallContextFields = useMemo(() => {
    console.log('[StepDetailsDialog] Computing filteredWaterfallContextFields:', {
      waterfallContextFieldsCount: waterfallContextFields.length,
      integration: integration?.vendor,
    });
    
    if (!waterfallContextFields.length) {
      console.log('[StepDetailsDialog] No waterfallContextFields, returning empty');
      return waterfallContextFields;
    }

    if (!integration) {
      const filtered = filterFieldsByVendor(waterfallContextFields, null);
      console.log('[StepDetailsDialog] No integration, returning generic fields:', filtered);
      return filtered;
    }

    const vendorName = integration.vendor;
    if (!vendorName) {
      const filtered = filterFieldsByVendor(waterfallContextFields, null);
      console.log('[StepDetailsDialog] No vendor name, returning generic fields:', filtered);
      return filtered;
    }

    const filtered = filterFieldsByVendor(waterfallContextFields, vendorName);
    console.log('[StepDetailsDialog] Filtered fields for vendor:', vendorName, filtered);
    return filtered;
  }, [integration, waterfallContextFields]);

  useEffect(() => {
    console.log('[StepDetailsDialog] Rules state changed:', rules);
    if (!isInitialLoad.current) {
      setHasRuleChanges(true);
    }

  }, [rules]);

  useEffect(() => {
    if (visible) {
      const fetchMetadata = async () => {
        try {
          console.log('=== Fetching Field Metadata ===');
          
          const fields = await waterfallService.getFieldMetadata(MetadataContextType.WaterfallContext);
          console.log('WaterfallContext fields (UNFILTERED - all vendors):', fields);
          setWaterfallContextFields(fields);
          
          const requestValueTargetFields = await waterfallService.getFieldMetadata(MetadataContextType.WaterfallRequestValueTarget);
          console.log('WaterfallRequestValueTarget fields (for override settings):', requestValueTargetFields);

          const allOperators = await waterfallService.getOperators();
          const filteredOperators = allOperators.filter(op => op.isLogical);
          setOperators(filteredOperators);
          
          
          console.log('=== Metadata Fetch Complete ===');
        } catch (error) {
          console.error('Failed to fetch field metadata:', error);
        }
      };
      
      fetchMetadata();
    }
  }, [visible, integration]);

  useEffect(() => {
    if (visible && integrationType && integrationType !== IntegrationType.None) {
      const fetchVendors = async () => {
        try {
          const settingsList = await IntegrationSettingsService.getInstance().getByType(integrationType);
          const options = settingsList.map((settings) => ({
            label: settings.name,
            value: settings.integrationSettingsId!,
          }));
          setVendorOptions(options);
        } catch (error) {
          console.error('Failed to fetch integration settings:', error);
          setVendorOptions([]);
        }
      };
      fetchVendors();
    }
  }, [visible, integrationType]);

  useEffect(() => {
    if (step && step.integrationSettingsId) {
      setIsLoadingIntegration(true);
      IntegrationSettingsService.getInstance()
        .get(step.integrationSettingsId.toString())
        .then(setIntegration)
        .catch(() => {
          setIntegration(null);
        })
        .finally(() => setIsLoadingIntegration(false));
    } else {
      setIntegration(null);
    }

    if (step?.waterfallRules && step.waterfallRules.length > 0) {
      setRules(step.waterfallRules.map((rule) => ({ ...rule })));
    } else {
      setRules([]);
    }

    if (step) {
      reset({
        description: step.description || '',
        integrationSettingsId: step.integrationSettingsId,
        stepTimeoutInSeconds: step.stepTimeoutInSeconds || undefined,
        onTimeoutExceededAction: step.onTimeoutExceededAction || WaterfallStepAction.Continue,
        onFatalErrorAction: step.onFatalErrorAction || WaterfallStepAction.Continue,
      });
      setHasRuleChanges(false);
      isInitialLoad.current = true;
      setTimeout(() => {
        isInitialLoad.current = false;
      }, 0);
    }
  }, [step, reset]);

  useEffect(() => {
    if (selectedVendorId && selectedVendorId > 0) {
      setIsLoadingIntegration(true);
      IntegrationSettingsService.getInstance()
        .get(selectedVendorId.toString())
        .then(setIntegration)
        .catch(() => {
          setIntegration(null);
        })
        .finally(() => setIsLoadingIntegration(false));
    } else if (selectedVendorId === 0) {
      setIntegration(null);
    }
  }, [selectedVendorId]);

  const handleAddRule = (ruleType: WaterfallRuleType) => {
    const newRule: WaterfallRuleViewModel = {
      ruleType,
      expression: ruleType === WaterfallRuleType.RequestSetting 
        ? null 
        : {
            logicalOperator: LogicalOperator.And,
            expressions: null,
            isNot: false,
          },
      overrideSetting: ruleType === WaterfallRuleType.RequestSetting
        ? { fieldName: null, value: null }
        : null,
    };
    setRules([...rules, newRule]);
    setHasRuleChanges(true);
  };

  const handleUpdateRuleExpression = (index: number, expression: SimpleExpression) => {
    console.log('[StepDetailsDialog] handleUpdateRuleExpression called:', { index, expression, currentRules: rules });
    
    const updatedRules = [...rules];
    updatedRules[index] = {
      ...updatedRules[index],
      expression,
    };
    
    console.log('[StepDetailsDialog] Updated rules:', updatedRules);
    setRules(updatedRules);
    setHasRuleChanges(true);
  };

  const handleUpdateRuleOverrideSetting = (index: number, overrideSetting: OverrideSettingViewModel | null) => {
    const updatedRules = [...rules];
    updatedRules[index] = {
      ...updatedRules[index],
      overrideSetting: overrideSetting || null,
    };
    setRules(updatedRules);
    setHasRuleChanges(true);
  };

  const handleDeleteRule = (index: number) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    setRules(updatedRules);
    setHasRuleChanges(true);
  };

  const getRuleTypeLabel = (ruleType: WaterfallRuleType): string => {
    switch (ruleType) {
      case WaterfallRuleType.StepCondition:
        return 'Step Condition';
      case WaterfallRuleType.ExitCondition:
        return 'Exit Condition';
      case WaterfallRuleType.RequestSetting:
        return 'Request Setting';
      default:
        return 'Unknown';
    }
  };

  const addRuleMenuItems = [
    {
      label: 'Step Condition',
      icon: 'pi pi-filter',
      command: () => handleAddRule(WaterfallRuleType.StepCondition),
    },
    {
      label: 'Exit Condition',
      icon: 'pi pi-sign-out',
      command: () => handleAddRule(WaterfallRuleType.ExitCondition),
    },
    {
      label: 'Request Setting',
      icon: 'pi pi-cog',
      command: () => handleAddRule(WaterfallRuleType.RequestSetting),
    },
  ];

  const handleFormSubmit = (data: StepFormValues) => {
    if (!step) return;

    const validRules = rules.filter((rule) => {
      if (rule.ruleType === WaterfallRuleType.RequestSetting) {
        return rule.overrideSetting && rule.overrideSetting.fieldName && rule.overrideSetting.value;
      } else {
        return rule.expression && rule.expression.expressions && rule.expression.expressions.length > 0;
      }
    });

    const updatedStep: StepViewModel = {
      ...step,
      description: data.description,
      integrationSettingsId: data.integrationSettingsId,
      stepTimeoutInSeconds: data.stepTimeoutInSeconds,
      onTimeoutExceededAction: data.onTimeoutExceededAction || undefined,
      onFatalErrorAction: data.onFatalErrorAction || undefined,
      waterfallRules: validRules.length > 0 ? validRules : null,
    };

    onSave(updatedStep);
    onClose();
  };

  if (!step) return null;

  const vendorName = integration?.name || (isLoadingIntegration ? 'Loading...' : 'Unknown Vendor');
  const stepDescription = step.description || 'Untitled Step';

  return (
    <Dialog
      visible={visible}
      onHide={onClose}
      header={
        <div className="flex items-center gap-2">
          <i className="pi pi-pencil"></i>
          <h3 className="m-0">Edit Step Details</h3>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" className="p-button-text" onClick={onClose} />
          <Button type="submit" label="Save" disabled={!isValid || (!isDirty && !hasRuleChanges)} form="step-details-form" />
        </div>
      }
      style={{ width: '90vw', maxWidth: '800px' }}
      modal
      dismissableMask
    >
      <form
        id="step-details-form"
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        className="p-fluid p-input-filled"
      >
        <div className="p-4">
          <div className="mb-4">
            <FormDropdown
              control={control}
              errors={errors}
              field="integrationSettingsId"
              label="Vendor"
              options={vendorOptions}
              required
              disabled={rules.length > 0}
            />
            {rules.length > 0 && (
              <small className="text-gray-500 block mt-1">
                <i className="pi pi-lock mr-1"></i>
                Delete all rules before changing vendor
              </small>
            )}
          </div>

          <div className="mb-4">
            <FormInputText
              control={control}
              errors={errors}
              field="description"
              label="Description"
              placeholder="Enter step description"
              required
            />
          </div>

          {selectedVendorId > 0 && (
            <>
              <hr className="my-4 border-t border-gray-300" />

              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="font-medium" style={{ display: 'block', marginBottom: '0' }}>
                    Rules
                  </label>
                  <div className="flex items-center gap-2">
                    <Menu model={addRuleMenuItems} popup ref={addRuleMenuRef} />
                    <Button
                      type="button"
                      label="Add Rule"
                      icon="pi pi-plus"
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        addRuleMenuRef.current?.toggle(e);
                      }}
                    />
                  </div>
                </div>

                {rules.length === 0 ? (
                  <div className="text-gray-500 text-sm italic p-3 border border-dashed border-gray-300 rounded">
                    No rules added. Click "Add Rule" to add a step condition, exit condition, or request setting.
                  </div>
                ) : (
                rules.map((rule, index) => (
                  <div key={index} className="mb-4 p-4 border border-gray-300 rounded">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-700">
                          {getRuleTypeLabel(rule.ruleType)}
                        </span>
                      </div>
                      <Button
                        icon="pi pi-trash"
                        className="p-button-text p-button-danger p-button-sm"
                        onClick={() => handleDeleteRule(index)}
                        tooltip="Delete rule"
                      />
                    </div>

                    {rule.ruleType === WaterfallRuleType.RequestSetting ? (
                      <OverrideSettingForm
                        initialValue={rule.overrideSetting || undefined}
                        onChange={(setting) => handleUpdateRuleOverrideSetting(index, setting)}
                        showDelete={false}
                      />
                    ) : (
                      <RuleGroup
                        group={rule.expression || {
                          logicalOperator: LogicalOperator.And,
                          expressions: null,
                          isNot: false,
                        }}
                        level={0}
                        onUpdate={(expression) => handleUpdateRuleExpression(index, expression)}
                        fieldMetadata={filteredWaterfallContextFields}
                        operators={operators}
                      />
                    )}


                    {rule.ruleType !== WaterfallRuleType.RequestSetting && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <label className="font-medium text-sm mb-2 block">Override Setting (Optional)</label>
                        <OverrideSettingForm
                          initialValue={rule.overrideSetting || undefined}
                          onChange={(setting) => handleUpdateRuleOverrideSetting(index, setting)}
                          showDelete={false}
                        />
                      </div>
                    )}
                  </div>
                ))
              )}
              </div>
            </>
          )}

          <hr className="my-4 border-t border-gray-300" />

          <div className="mb-4">
            <FormInputNumber
              control={control}
              errors={errors}
              field="stepTimeoutInSeconds"
              label="Step Timeout (seconds)"
              placeholder="Enter timeout in seconds"
            />
          </div>

          <div className="mb-4">
            <FormRadioGroup
              control={control}
              errors={errors}
              field="onTimeoutExceededAction"
              label="On Timeout Exceeded"
              options={actionOptions}
            />
          </div>

          <div className="mb-6">
            <FormRadioGroup
              control={control}
              errors={errors}
              field="onFatalErrorAction"
              label="On Fatal Error"
              options={actionOptions}
            />
          </div>

        </div>
      </form>
    </Dialog>
  );
};
